import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Alert, Spinner } from 'react-bootstrap';
// import { useWorkspace } from '../../../Context/WorkspaceContext'; // Unused for now
import { PrimaryButton, CancelButton } from '../../../Components/Buttons';
import { getAppDisplayName, getAppColor } from '../bvUtils';
import { FiLink, FiXCircle } from 'react-icons/fi';
import { useLanguage } from '../../../Context/LanguageContext';
import { useGrandfatheredAccess } from '../../../hooks/useGrandfatheredAccess';
import supportIcon from '../../../assets/characters/support-gray.webp';
import imageGenIcon from '../../../assets/characters/image-gen-gray.webp';
import socialMediaIcon from '../../../assets/characters/social-media-gray.webp';
import videoGenIcon from '../../../assets/characters/video-gray.webp';
import seoIcon from '../../../assets/characters/seo-gray.webp';
import leadIcon from '../../../assets/characters/lead-gray.webp';
import '../training-process.css';

// Helper function to get app icon
const getAppIcon = (appName) => {
  const name = appName.toLowerCase();
  if (name.includes('video')) return videoGenIcon;
  if (name.includes('image') || name.includes('gen')) return imageGenIcon;
  if (name.includes('social')) return socialMediaIcon;
  if (name.includes('seo')) return seoIcon;
  if (name.includes('lead')) return leadIcon;
  return supportIcon; // fallback
};

// Helper function to get color filter for app icons
const getAppIconFilter = (appName) => {
  const name = appName.toLowerCase();
  if (name.includes('video')) {
    // Video Gen BV - Cyan tint (#0D5A6F)
    return 'brightness(0.95) sepia(0.15) hue-rotate(180deg) saturate(1.5)';
  }
  if (name.includes('image') || name.includes('gen')) {
    // Image Gen BV - Orange/Brown tint (#752E16)
    return 'brightness(0.95) sepia(0.15) hue-rotate(15deg) saturate(1.5)';
  }
  if (name.includes('social')) {
    // Social Media BV - Blue tint (#1B3576)
    return 'brightness(0.95) sepia(0.15) hue-rotate(210deg) saturate(1.5)';
  }
  if (name.includes('seo')) {
    // SEO BV - Red tint (#6F1C1B)
    return 'brightness(0.95) sepia(0.15) hue-rotate(350deg) saturate(1.5)';
  }
  if (name.includes('lead')) {
    // Lead BV - Green tint (#167516)
    return 'brightness(0.95) sepia(0.15) hue-rotate(120deg) saturate(1.5)';
  }
  // Fallback - Purple tint for support
  return 'brightness(0.95) sepia(0.15) hue-rotate(240deg) saturate(1.5)';
};

const AssignmentModal = ({ show, onHide, workspaceId, apiUrl, selectedContents = [], initialAssignments = { chatbots: [], apps: [] }, onAssignSuccess }) => {
  // const { workspaceData } = useWorkspace(); // Unused for now
  const { t } = useLanguage();
  const { hasSeoBVAccess, hasLeadBVAccess } = useGrandfatheredAccess();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [chatbots, setChatbots] = useState([]);
  const [selectedChatbots, setSelectedChatbots] = useState([]);
  const [apps, setApps] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [actionType, setActionType] = useState('assign');

  // Memoize filtered apps to prevent unnecessary re-renders
  const filteredApps = useMemo(() => {
    return apps.filter(app => {
      const name = app.name?.toLowerCase() || '';
      if (name.includes('seo') && !hasSeoBVAccess) return false;
      if (name.includes('lead') && !hasLeadBVAccess) return false;
      return true;
    });
  }, [apps, hasSeoBVAccess, hasLeadBVAccess]);

  // Refs to prevent duplicate fetches
  const hasFetchedChatbotsRef = useRef(false);
  const hasFetchedAppsRef = useRef(false);
  const lastShowRef = useRef(false);

  // Reset refs when modal opens (allow fresh fetch on each open)
  useEffect(() => {
    if (show && !lastShowRef.current) {
      // Modal just opened - reset fetch refs
      hasFetchedChatbotsRef.current = false;
      hasFetchedAppsRef.current = false;
    }
    lastShowRef.current = show;
  }, [show]);

  // Fetch chatbots when modal is shown
  useEffect(() => {
    const fetchChatbots = async () => {
      if (!show || !workspaceId || hasFetchedChatbotsRef.current) return;

      hasFetchedChatbotsRef.current = true;
      try {
        const brandvoxToken = localStorage.getItem('brandvox_token');
        const response = await fetch(
          `${apiUrl}/api/v1/chatbot/get-chatbots`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${brandvoxToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ workspace_id: workspaceId })
          }
        );
        if (response.ok) {
          const data = await response.json();
          setChatbots(data.chatbots || []);
        }
      } catch (error) {
        setChatbots([]);
        hasFetchedChatbotsRef.current = false; // Allow retry on error
      }
    };

    fetchChatbots();
  }, [show, workspaceId, apiUrl]);

  // Fetch apps when modal is shown
  useEffect(() => {
    const fetchApps = async () => {
      if (!show || hasFetchedAppsRef.current) return;

      hasFetchedAppsRef.current = true;
      try {
        const brandvoxToken = localStorage.getItem('brandvox_token');
        const response = await fetch(
          `${apiUrl}/api/v1/apps/get-apps`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${brandvoxToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          setApps(data.apps || []);
        }
      } catch (error) {
        setApps([]);
        hasFetchedAppsRef.current = false; // Allow retry on error
      }
    };

    fetchApps();
  }, [show, apiUrl]);

  // Memoize initial assignment IDs to prevent effect re-runs from object reference changes
  const initialChatbotIds = useMemo(() => {
    return (initialAssignments.chatbots || []).join(',');
  }, [initialAssignments.chatbots]);

  const initialAppIds = useMemo(() => {
    return (initialAssignments.apps || []).join(',');
  }, [initialAssignments.apps]);

  useEffect(() => {
    if (show) {
      // Pre-select common assignments from initialAssignments
      setSelectedChatbots(initialChatbotIds ? initialChatbotIds.split(',').filter(id => id) : []);
      setSelectedApps(initialAppIds ? initialAppIds.split(',').filter(id => id) : []);
      setError(null);
      setInfo(null);
      setActionType('assign');
    }
  }, [show, initialChatbotIds, initialAppIds]);

  const handleChatbotToggle = (chatbotId) => {
    setSelectedChatbots(prev =>
      prev.includes(chatbotId)
        ? prev.filter(id => id !== chatbotId)
        : [...prev, chatbotId]
    );
  };

  const handleAppToggle = (appId) => {
    setSelectedApps(prev =>
      prev.includes(appId)
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleSelectAll = () => {
    const validChatbots = chatbots.filter(bot => bot.chatbot_id);
    if (selectedChatbots.length === validChatbots.length) {
      setSelectedChatbots([]);
    } else {
      setSelectedChatbots(validChatbots.map(bot => bot.chatbot_id));
    }
  };

  const handleSelectAllApps = () => {
    const validApps = filteredApps.filter(app => app.app_id);
    if (selectedApps.length === validApps.length) {
      setSelectedApps([]);
    } else {
      setSelectedApps(validApps.map(app => app.app_id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedContents.length === 0) {
      setError(t('dashboard.training.modals.assignmentmodal.errorNoContents'));
      return;
    }
    if (selectedChatbots.length === 0 && selectedApps.length === 0) {
      setError(t('dashboard.training.modals.assignmentmodal.errorNoBVs'));
      return;
    }

    setLoading(true);
    setError(null);
    setInfo(null);

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const endpoint = actionType === 'assign'
        ? '/api/v1/train/assign-contents-to-chatbots-and-apps'
        : '/api/v1/train/unassign-contents-from-chatbots-and-apps';

      const body = {
        workspace_id: workspaceId,
        content_names: selectedContents,
        assigned_to: {
          chatbots: selectedChatbots,
          apps: selectedApps
        }
      };

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorKey = actionType === 'assign'
          ? 'dashboard.training.modals.assignmentmodal.errorAssignFailed'
          : 'dashboard.training.modals.assignmentmodal.errorUnassignFailed';
        throw new Error(errorData.detail || t(errorKey));
      }

      const responseData = await response.json();

      // Check if no action was needed (all already assigned/unassigned)
      if (responseData.message) {
        if (responseData.message.includes('No new assignments needed') ||
          responseData.message.includes('No unassignments needed')) {
          // Show info message to user
          const infoMessage = actionType === 'assign'
            ? t('dashboard.training.modals.assignmentmodal.infoAlreadyAssigned')
            : t('dashboard.training.modals.assignmentmodal.infoNoneAssigned');

          setInfo(infoMessage);

          // Still call success callback and close after a delay
          setTimeout(() => {
            if (onAssignSuccess) {
              onAssignSuccess();
            }
            onHide();
          }, 2000);
          return;
        }
      }

      if (onAssignSuccess) {
        onAssignSuccess();
      }

      onHide();
    } catch (err) {
      const fallbackKey = actionType === 'assign'
        ? 'dashboard.training.modals.assignmentmodal.errorAssignFailed'
        : 'dashboard.training.modals.assignmentmodal.errorUnassignFailed';
      setError(err.message || t(fallbackKey));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
      <Modal.Body className="p-0 modal-body" style={{ height: '90vh', overflow: 'hidden', padding: 0 }}>
        <div className='training-process-container' style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Professional header */}
          <div className='training-process-header'>
            <div className='header-content'>
              <div className='header-text'>
                <h3>
                  {actionType === 'assign'
                    ? t('dashboard.training.modals.assignmentmodal.titleAssign')
                    : t('dashboard.training.modals.assignmentmodal.titleUnassign')
                  }
                </h3>
              </div>
              <div className='character-count-display'>
                <strong>{t('dashboard.training.modals.assignmentmodal.contentCount', { count: selectedContents.length })}</strong>
                <span className='ms-2'>
                  {selectedContents.length === 1
                    ? t('dashboard.training.modals.assignmentmodal.contentLabel')
                    : t('dashboard.training.modals.assignmentmodal.contentsLabel')
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className='training-process-content'>
            {error && (
              <Alert variant="danger" onClose={() => setError(null)} dismissible>
                {error}
              </Alert>
            )}

            {info && (
              <Alert variant="info" onClose={() => setInfo(null)} dismissible>
                {info}
              </Alert>
            )}

            {/* Action Type Selection */}
            <div className="chatbot-selection-section mb-4">
              <div className="selection-header">
                <h5>{t('dashboard.training.modals.assignmentmodal.actionTitle')}</h5>
              </div>
              <div className="chatbot-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                <label className={`chatbot-option ${actionType === 'assign' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="action"
                    checked={actionType === 'assign'}
                    style={{ display: 'none' }}
                    onChange={() => setActionType('assign')}
                  />
                  <span className="chatbot-name">{t('dashboard.training.modals.assignmentmodal.actionAssign')}</span>
                  <span className="checkmark">✓</span>
                </label>
                <label className={`chatbot-option ${actionType === 'unassign' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="action"
                    checked={actionType === 'unassign'}
                    style={{ display: 'none' }}
                    onChange={() => setActionType('unassign')}
                  />
                  <span className="chatbot-name">{t('dashboard.training.modals.assignmentmodal.actionUnassign')}</span>
                  <span className="checkmark">✓</span>
                </label>
              </div>
            </div>

            {/* Selected Contents Display */}
            <div className="chatbot-selection-section mb-4">
              <div className="selection-header">
                <h5>{t('dashboard.training.modals.assignmentmodal.selectedContentsTitle', { count: selectedContents.length })}</h5>
              </div>
              <div className="training-items-container" style={{ maxHeight: '20vh' }}>
                {selectedContents.length === 0 ? (
                  <p className="no-items-message">
                    {t('dashboard.training.modals.assignmentmodal.noContentsSelected')}
                  </p>
                ) : (
                  selectedContents.map((content, index) => (
                    <div key={index} className='training-item'>
                      <div className="item-info">
                        <div className="item-key">{content}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Chatbot Selection */}
            <div className="chatbot-selection-section mb-3">
              <div className="selection-header">
                <h5>{t('dashboard.training.modals.assignmentmodal.supportBVsTitle')}</h5>
                <div className="selection-actions">
                  <button
                    type="button"
                    className="selection-action-btn"
                    onClick={handleSelectAll}
                  >
                    {selectedChatbots.length === chatbots.filter(bot => bot.chatbot_id).length
                      ? t('dashboard.training.modals.assignmentmodal.deselectAllButton')
                      : t('dashboard.training.modals.assignmentmodal.selectAllButton')
                    }
                  </button>
                </div>
              </div>
              <p className="selection-description">
                {actionType === 'assign'
                  ? t('dashboard.training.modals.assignmentmodal.supportBVsDescriptionAssign')
                  : t('dashboard.training.modals.assignmentmodal.supportBVsDescriptionUnassign')
                }
              </p>

              {chatbots.length === 0 ? (
                <p className="no-items-message">
                  {t('dashboard.training.modals.assignmentmodal.noSupportBVs')}
                </p>
              ) : (
                <div className="chatbot-grid">
                  {chatbots.filter(chatbot => chatbot.chatbot_id).map((chatbot) => {
                    const isSelected = selectedChatbots.includes(chatbot.chatbot_id);
                    return (
                      <label
                        key={chatbot.chatbot_id}
                        className={`chatbot-option support-bv ${isSelected ? 'selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleChatbotToggle(chatbot.chatbot_id)}
                        />
                        <img
                          src={supportIcon}
                          alt="Support BV"
                          className="bv-icon me-2"
                          style={{
                            width: '24px',
                            height: '24px',
                            filter: isSelected ? 'brightness(0.95) sepia(0.15) hue-rotate(240deg) saturate(1.5)' : 'none',
                            transition: 'filter 0.2s ease'
                          }}
                        />
                        <span className="chatbot-name">{chatbot.chatbot_name || chatbot.chatbot_id}</span>
                        <span className="checkmark">✓</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* App Selection */}
            <div className="chatbot-selection-section mb-4">
              <div className="selection-header">
                <h5>{t('dashboard.training.modals.assignmentmodal.otherBVsTitle')}</h5>
                <div className="selection-actions">
                  <button
                    type="button"
                    className="selection-action-btn"
                    onClick={handleSelectAllApps}
                  >
                    {selectedApps.length === filteredApps.filter(app => app.app_id).length
                      ? t('dashboard.training.modals.assignmentmodal.deselectAllButton')
                      : t('dashboard.training.modals.assignmentmodal.selectAllButton')
                    }
                  </button>
                </div>
              </div>
              <p className="selection-description">
                {actionType === 'assign'
                  ? t('dashboard.training.modals.assignmentmodal.otherBVsDescriptionAssign')
                  : t('dashboard.training.modals.assignmentmodal.otherBVsDescriptionUnassign')
                }
              </p>

              {filteredApps.length === 0 ? (
                <p className="no-items-message">
                  {t('dashboard.training.modals.assignmentmodal.noOtherBVs')}
                </p>
              ) : (
                <div className="chatbot-grid">
                  {filteredApps.filter(app => app.app_id).map((app) => {
                    const appColor = getAppColor(app.name);
                    const isSelected = selectedApps.includes(app.app_id);
                    const appIcon = getAppIcon(app.name);
                    const appIconFilter = getAppIconFilter(app.name);
                    return (
                      <label
                        key={app.app_id}
                        className={`chatbot-option app-bv ${isSelected ? 'selected' : ''}`}
                        style={isSelected ? {
                          borderColor: appColor,
                          background: `${appColor}14`,
                          boxShadow: `0 0 0 2px ${appColor}1A`
                        } : {}}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleAppToggle(app.app_id)}
                        />
                        <img
                          src={appIcon}
                          alt={app.name}
                          className="bv-icon me-2"
                          style={{
                            width: '24px',
                            height: '24px',
                            filter: isSelected ? appIconFilter : 'none',
                            transition: 'filter 0.2s ease'
                          }}
                        />
                        <span className="chatbot-name" style={isSelected ? { color: appColor } : {}}>
                          {getAppDisplayName(app.name) || app.app_id}
                        </span>
                        <span className="checkmark" style={isSelected ? {
                          background: appColor,
                          borderColor: appColor
                        } : {}}>✓</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

          </div>

          {/* Action buttons footer */}
          <div className='training-process-footer'>
            <CancelButton
              onClick={onHide}
              disabled={loading}
              style={{ marginRight: '8px' }}
            >
              {t('dashboard.training.modals.assignmentmodal.cancelButton')}
            </CancelButton>
            <PrimaryButton
              onClick={handleSubmit}
              disabled={loading || selectedContents.length === 0 || (selectedChatbots.length === 0 && selectedApps.length === 0)}
              backgroundColor={actionType === 'assign' ? 'var(--primary)' : 'var(--danger)'}
              borderColor={actionType === 'assign' ? 'var(--primary)' : 'var(--danger)'}
              hoverBackgroundColor={actionType === 'assign' ? 'var(--primary-hover)' : 'var(--danger-hover)'}
              hoverBorderColor={actionType === 'assign' ? 'var(--primary-hover)' : 'var(--danger-hover)'}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {actionType === 'assign'
                    ? t('dashboard.training.modals.assignmentmodal.assigningButton')
                    : t('dashboard.training.modals.assignmentmodal.unassigningButton')
                  }
                </>
              ) : (
                <>
                  {actionType === 'assign' ? <FiLink className="me-2" /> : <FiXCircle className="me-2" />}
                  {actionType === 'assign'
                    ? t('dashboard.training.modals.assignmentmodal.assignButton')
                    : t('dashboard.training.modals.assignmentmodal.unassignButton')
                  }
                </>
              )}
            </PrimaryButton>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AssignmentModal;